import { useEffect, useMemo, useState } from "react";
import {
  getPartnersPage,
  savePartnersPage,
} from "../../data/cmsStore";
import { cmsAdminApi } from "../../api/cmsApi";

const emptyPartner = {
  id: "",
  categoryId: "",
  name: "",
  image: "",
  link: "",
  hoverText: "",
  sortOrder: 0,
  status: "active",
};

const MAX_LOGO_BYTES = 1.2 * 1024 * 1024;

function resolvePreview(src) {
  if (!src) return "";
  if (src.startsWith("data:") || src.startsWith("http")) return src;
  return `${process.env.PUBLIC_URL || ""}${src}`;
}

export default function AdminPartnersPage() {
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pageCopy, setPageCopy] = useState(() => getPartnersPage());
  const [form, setForm] = useState(emptyPartner);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [copySaved, setCopySaved] = useState(false);
  const [error, setError] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const [partnersData, categoryData] = await Promise.all([
        cmsAdminApi.listPartners(),
        cmsAdminApi.listPartnerCategories(),
      ]);
      setPartners(partnersData);
      setCategories(categoryData);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load partners");
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const seedAllPartners = async () => {
    if (
      !window.confirm(
        "Load all previous partner categories and logos into the database? Existing matching IDs will be updated."
      )
    ) {
      return;
    }
    setSeeding(true);
    try {
      const result = await cmsAdminApi.seedPartners();
      await refresh();
      window.alert(
        `Saved to database.\nCategories: ${result.partnerCategories}\nPartners: ${result.partners}`
      );
    } catch (e) {
      setError(e.message || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const filtered =
    filterCat === "all" ? partners : partners.filter((p) => p.categoryId === filterCat);

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyPartner);
  };

  const openCreate = () => {
    setForm({
      ...emptyPartner,
      categoryId: filterCat !== "all" ? filterCat : categories[0]?.id || "",
      sortOrder: partners.length,
    });
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setForm({ ...emptyPartner, ...p });
    setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);

  const onSavePartner = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await cmsAdminApi.savePartner({
        ...form,
        id: form.id || undefined,
        name: form.name.trim(),
        image: form.image.trim(),
        link: form.link.trim(),
        hoverText: form.hoverText.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      });
      closeModal();
      await refresh();
    } catch (err) {
      setError(err.message || "Could not save partner to database");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this partner logo from database?")) return;
    setError("");
    try {
      await cmsAdminApi.deletePartner(id);
      await refresh();
    } catch (err) {
      setError(err.message || "Could not delete partner");
    }
  };

  const onSaveCopy = (e) => {
    e.preventDefault();
    savePartnersPage(pageCopy);
    setCopySaved(true);
    window.setTimeout(() => setCopySaved(false), 2000);
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      window.alert("Logo must be under 1.2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const previewSrc = resolvePreview(form.image);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>Partners</h1>
          <p>Edit page text, logos, website links, and hover descriptions.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            onClick={seedAllPartners}
            disabled={seeding}
          >
            <i className="bi bi-database-down" /> {seeding ? "Seeding…" : "Load all logos to DB"}
          </button>
          <button type="button" className="admin-btn admin-btn-primary" onClick={openCreate}>
            <i className="bi bi-plus-lg" /> Add partner
          </button>
        </div>
      </header>
      {error ? <p className="text-danger">{error}</p> : null}

      <form className="admin-panel admin-form" onSubmit={onSaveCopy}>
        <div className="admin-panel-head">
          <h2>Partners page text</h2>
          {copySaved ? <span className="admin-inline-ok">Saved</span> : null}
        </div>
        <div className="admin-form-grid">
          <label>
            Hero label
            <input
              value={pageCopy.heroLabel || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, heroLabel: e.target.value })}
            />
          </label>
          <label>
            Hero title (before accent)
            <input
              value={pageCopy.heroTitleBefore || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, heroTitleBefore: e.target.value })}
            />
          </label>
          <label>
            Hero accent text
            <input
              value={pageCopy.heroTitleAccent || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, heroTitleAccent: e.target.value })}
            />
          </label>
          <label className="admin-form-span">
            Hero description
            <textarea
              rows={2}
              value={pageCopy.heroDesc || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, heroDesc: e.target.value })}
            />
          </label>
          <label>
            Intro label
            <input
              value={pageCopy.introLabel || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, introLabel: e.target.value })}
            />
          </label>
          <label>
            Intro title
            <input
              value={pageCopy.introTitle || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, introTitle: e.target.value })}
            />
          </label>
          <label className="admin-form-span">
            Intro paragraph 1
            <textarea
              rows={2}
              value={pageCopy.introText1 || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, introText1: e.target.value })}
            />
          </label>
          <label className="admin-form-span">
            Intro paragraph 2
            <textarea
              rows={2}
              value={pageCopy.introText2 || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, introText2: e.target.value })}
            />
          </label>
          <label className="admin-form-span">
            Intro image URL / path
            <input
              value={pageCopy.introImage || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, introImage: e.target.value })}
              placeholder="/img/partners-section.jpg"
            />
          </label>
          <label>
            CTA title
            <input
              value={pageCopy.ctaTitle || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, ctaTitle: e.target.value })}
            />
          </label>
          <label>
            CTA button
            <input
              value={pageCopy.ctaButton || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, ctaButton: e.target.value })}
            />
          </label>
          <label className="admin-form-span">
            CTA description
            <textarea
              rows={2}
              value={pageCopy.ctaDesc || ""}
              onChange={(e) => setPageCopy({ ...pageCopy, ctaDesc: e.target.value })}
            />
          </label>
        </div>
        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary">
            Save page text
          </button>
        </div>
      </form>

      <div className="admin-panel mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <span className="text-muted small text-uppercase fw-bold me-1">Filter</span>
          <button
            type="button"
            className={`admin-btn admin-btn-sm${filterCat === "all" ? " admin-btn-primary" : " admin-btn-ghost"}`}
            onClick={() => setFilterCat("all")}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`admin-btn admin-btn-sm${filterCat === c.id ? " admin-btn-primary" : " admin-btn-ghost"}`}
              onClick={() => setFilterCat(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-panel">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Name</th>
                <th>Category</th>
                <th>Link</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const src = resolvePreview(p.image);
                return (
                  <tr key={p.id}>
                    <td>
                      {src ? (
                        <img className="admin-table-logo" src={src} alt="" />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <strong>{p.name}</strong>
                      {p.hoverText ? (
                        <div className="text-muted small">
                          {p.hoverText.slice(0, 72)}
                          {p.hoverText.length > 72 ? "…" : ""}
                        </div>
                      ) : null}
                    </td>
                    <td>{catName(p.categoryId)}</td>
                    <td>
                      {p.link ? (
                        <a href={p.link} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{p.status}</td>
                    <td className="admin-actions">
                      <button type="button" className="admin-btn admin-btn-sm" onClick={() => openEdit(p)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-sm admin-btn-danger"
                        onClick={() => onDelete(p.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="admin-empty">
                    {partners.length === 0
                      ? "Database empty — click “Load all logos to DB” to import previous partners, or Add partner."
                      : "No partners in this filter."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen ? (
        <div className="admin-modal-root" role="presentation">
          <button type="button" className="admin-modal-backdrop" aria-label="Close" onClick={closeModal} />
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-partner-modal-title"
          >
            <form className="admin-modal-card admin-form" onSubmit={onSavePartner}>
              <header className="admin-modal-head">
                <div className="admin-modal-head-text">
                  <span className="admin-modal-badge">{form.id ? "Edit" : "New"}</span>
                  <h2 id="admin-partner-modal-title">{form.id ? "Edit partner" : "Add partner"}</h2>
                  <p>Upload logo, set website link, and category details.</p>
                </div>
                <button type="button" className="admin-modal-close" aria-label="Close" onClick={closeModal}>
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </header>

              <div className="admin-modal-body">
                <div className="admin-partner-modal-layout">
                  <div className="admin-partner-logo-pane">
                    <div className={`admin-partner-logo-box${previewSrc ? " has-image" : ""}`}>
                      {previewSrc ? (
                        <img src={previewSrc} alt="" />
                      ) : (
                        <div className="admin-partner-logo-empty">
                          <i className="bi bi-image" aria-hidden="true" />
                          <span>Logo preview</span>
                        </div>
                      )}
                    </div>
                    <label className="admin-partner-upload-btn">
                      <i className="bi bi-upload" aria-hidden="true" />
                      Upload logo
                      <input type="file" accept="image/*" onChange={onLogoFile} hidden />
                    </label>
                    <p className="admin-partner-upload-hint">PNG / SVG / JPG · max 1.2 MB</p>
                  </div>

                  <div className="admin-partner-fields">
                    <label>
                      Partner name
                      <input
                        required
                        autoFocus
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="e.g. Fortinet"
                      />
                    </label>

                    <div className="admin-partner-fields-row">
                      <label>
                        Category
                        <select
                          required
                          value={form.categoryId}
                          onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                        >
                          <option value="">Select category</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Status
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                          <option value="active">Active</option>
                          <option value="hidden">Hidden</option>
                        </select>
                      </label>
                    </div>

                    <div className="admin-partner-fields-row">
                      <label>
                        Website link
                        <input
                          type="url"
                          value={form.link}
                          onChange={(e) => setForm({ ...form, link: e.target.value })}
                          placeholder="https://example.com"
                        />
                      </label>
                      <label>
                        Sort order
                        <input
                          type="number"
                          value={form.sortOrder}
                          onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                        />
                      </label>
                    </div>

                    <label>
                      Logo path or URL
                      <input
                        value={form.image.startsWith("data:") ? "" : form.image}
                        onChange={(e) => setForm({ ...form, image: e.target.value })}
                        placeholder="/img/cybersecurity/1.png"
                      />
                    </label>

                    <label>
                      Hover / popup text
                      <textarea
                        rows={2}
                        value={form.hoverText}
                        onChange={(e) => setForm({ ...form, hoverText: e.target.value })}
                        placeholder="Short description shown on hover"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <footer className="admin-modal-foot">
                <button type="button" className="admin-btn admin-btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  <i className={`bi ${form.id ? "bi-check-lg" : "bi-plus-lg"}`} aria-hidden="true" />
                  {saving ? "Saving to DB…" : form.id ? "Save changes" : "Add partner"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
